/**
 * Support Module - React Query Hooks
 * Custom hooks for fetching and managing support data with React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * Hook to fetch support tickets with filters
 */
export const useTickets = (filters = {}) => {
  const {
    status,
    priority,
    category,
    assignee_user_id,
    requester_user_id,
    tags,
    search,
    skip = 0,
    limit = 20,
    sort = '-created_at'
  } = filters;

  // Build query params
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (priority) params.append('priority', priority);
  if (category) params.append('category', category);
  if (assignee_user_id) params.append('assignee_user_id', assignee_user_id);
  if (requester_user_id) params.append('requester_user_id', requester_user_id);
  if (tags) params.append('tags', tags);
  if (search) params.append('search', search);
  params.append('skip', skip);
  params.append('limit', limit);
  params.append('sort', sort);

  return useQuery({
    queryKey: ['tickets', filters],
    queryFn: async () => {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/support/tickets?${params.toString()}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch tickets');
      }
      
      return response.json();
    },
    keepPreviousData: true, // Keep previous data while fetching new data
  });
};

/**
 * Hook to fetch a single ticket by ID
 */
export const useTicket = (ticketId) => {
  return useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: async () => {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/support/tickets/${ticketId}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch ticket');
      }
      
      return response.json();
    },
    enabled: !!ticketId, // Only run query if ticketId is provided
  });
};

/**
 * Hook to create a new ticket
 */
export const useCreateTicket = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (ticketData) => {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/support/tickets`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(ticketData),
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create ticket');
      }
      
      return response.json();
    },
    onSuccess: () => {
      // Invalidate tickets list to refetch
      queryClient.invalidateQueries(['tickets']);
    },
  });
};

/**
 * Hook to update a ticket
 */
export const useUpdateTicket = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ ticketId, updates }) => {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/support/tickets/${ticketId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updates),
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update ticket');
      }
      
      return response.json();
    },
    onSuccess: (data, variables) => {
      // Invalidate both the ticket list and the specific ticket
      queryClient.invalidateQueries(['tickets']);
      queryClient.invalidateQueries(['ticket', variables.ticketId]);
    },
  });
};

/**
 * Hook to add a reply to a ticket
 */
export const useAddReply = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ ticketId, replyData }) => {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/support/tickets/${ticketId}/replies`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(replyData),
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add reply');
      }
      
      return response.json();
    },
    onSuccess: (data, variables) => {
      // Invalidate the ticket to refetch with new reply
      queryClient.invalidateQueries(['ticket', variables.ticketId]);
      queryClient.invalidateQueries(['tickets']);
    },
  });
};

/**
 * Hook to fetch KB articles with filters
 */
export const useKBArticles = (filters = {}) => {
  const {
    status,
    category,
    tags,
    search,
    skip = 0,
    limit = 20,
    sort = '-created_at'
  } = filters;

  // Build query params
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (category) params.append('category', category);
  if (tags) params.append('tags', tags);
  if (search) params.append('search', search);
  params.append('skip', skip);
  params.append('limit', limit);
  params.append('sort', sort);

  return useQuery({
    queryKey: ['kb-articles', filters],
    queryFn: async () => {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/support/kb?${params.toString()}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch articles');
      }
      
      return response.json();
    },
    keepPreviousData: true,
  });
};

/**
 * Hook to fetch a single KB article by ID
 */
export const useKBArticle = (articleId) => {
  return useQuery({
    queryKey: ['kb-article', articleId],
    queryFn: async () => {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/support/kb/${articleId}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch article');
      }
      
      return response.json();
    },
    enabled: !!articleId,
  });
};

/**
 * Hook to create a KB article
 */
export const useCreateKBArticle = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (articleData) => {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/support/kb`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(articleData),
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create article');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['kb-articles']);
    },
  });
};

/**
 * Hook to fetch support team members
 */
export const useSupportTeam = (filters = {}) => {
  const { is_active, is_available, role } = filters;

  // Build query params
  const params = new URLSearchParams();
  if (is_active !== undefined) params.append('is_active', is_active);
  if (is_available !== undefined) params.append('is_available', is_available);
  if (role) params.append('role', role);

  return useQuery({
    queryKey: ['support-team', filters],
    queryFn: async () => {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/support/team?${params.toString()}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch team members');
      }
      
      return response.json();
    },
  });
};

/**
 * Hook to fetch support statistics
 */
export const useSupportStats = () => {
  return useQuery({
    queryKey: ['support-stats'],
    queryFn: async () => {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/support/stats`,
        { credentials: 'include' }
      );
      
      // If endpoint doesn't exist yet, return default stats
      if (response.status === 404) {
        return {
          success: true,
          stats: {
            total_tickets: 0,
            open_tickets: 0,
            resolved_today: 0,
            avg_response_time: 0,
            kb_articles: 0,
            team_members: 0
          }
        };
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      
      return response.json();
    },
    refetchInterval: 60000, // Auto-refetch every 60 seconds
  });
};
